import {
  Body,
  Controller,
  Delete,
  Get,
  Param,
  Patch,
  Post,
  Query,
  UploadedFile,
  UseGuards,
  UseInterceptors,
} from '@nestjs/common';
import { FileInterceptor } from '@nestjs/platform-express';
import { ApiBearerAuth, ApiSecurity, ApiTags } from '@nestjs/swagger';
import { AdminAccessGuard } from '../auth/admin-access.guard';
import { AuthGuard } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { CommerceAdminService, RawQuery } from './commerce-admin.service';

@ApiTags('Admin · Platform')
@ApiBearerAuth()
@ApiSecurity('store-id')
@Controller('admin')
@UseGuards(AuthGuard, AdminAccessGuard, DemoModeGuard)
export class CommerceAdminController {
  constructor(private readonly commerceAdminService: CommerceAdminService) {}

  @Get('platform/organizations')
  organizations(@Query() query: RawQuery) {
    return this.commerceAdminService.listOrganizations(query);
  }

  @Post('platform/organizations')
  createOrganization(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createOrganization(body);
  }

  // These three must stay registered before the generic
  // 'platform/organizations/:organizationId' route right below — Nest/
  // Express match routes in declaration order, so if the :organizationId
  // wildcard came first it would swallow requests to this literal path,
  // treating "usage"/"image-settings" as an organizationId and 404ing with
  // "organization not found" before ever reaching these handlers. (This bit
  // us for real: PATCH .../image-settings 404'd until this reordering.)
  @Get('platform/organizations/usage')
  imageUsage() {
    return this.commerceAdminService.getImageUsage();
  }

  @Get('platform/organizations/image-settings')
  imageSettings() {
    return this.commerceAdminService.getImageSettings();
  }

  @Patch('platform/organizations/image-settings')
  updateImageSettings(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.updateImageSettings(body);
  }

  @Patch('platform/organizations/:organizationId')
  updateOrganization(
    @Param('organizationId') organizationId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateOrganization(organizationId, body);
  }

  @Delete('platform/organizations/:organizationId')
  deleteOrganization(@Param('organizationId') organizationId: string) {
    return this.commerceAdminService.deleteOrganization(organizationId);
  }

  @Get('platform/stores')
  stores(@Query() query: RawQuery) {
    return this.commerceAdminService.listStores(query);
  }

  @Post('platform/stores')
  createStore(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createStore(body);
  }

  @Patch('platform/stores/:storeId')
  updateStore(
    @Param('storeId') storeId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateStore(storeId, body);
  }

  @Delete('platform/stores/:storeId')
  deleteStore(@Param('storeId') storeId: string) {
    return this.commerceAdminService.deleteStore(storeId);
  }

  @Get('store/staff')
  storeStaff(@Query() query: RawQuery) {
    return this.commerceAdminService.listStoreStaff(query);
  }

  @Post('store/staff')
  addStoreStaff(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.addStoreStaff(body);
  }

  @Patch('store/staff/:staffId')
  updateStoreStaff(
    @Param('staffId') staffId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateStoreStaff(staffId, body);
  }

  @Delete('store/staff/:staffId')
  removeStoreStaff(@Param('staffId') staffId: string) {
    return this.commerceAdminService.removeStoreStaff(staffId);
  }

  @Get('customers')
  customers(@Query() query: RawQuery) {
    return this.commerceAdminService.listCustomers(query);
  }

  @Post('customers')
  createCustomer(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createCustomer(body);
  }

  @Patch('customers/:customerId')
  updateCustomer(
    @Param('customerId') customerId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateCustomer(customerId, body);
  }

  @Delete('customers/:customerId')
  deleteCustomer(@Param('customerId') customerId: string) {
    return this.commerceAdminService.deleteCustomer(customerId);
  }

  @Get('customers/:customerId/addresses')
  customerAddresses(
    @Param('customerId') customerId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listCustomerAddresses(customerId, query);
  }

  @Post('customers/:customerId/addresses')
  createCustomerAddress(
    @Param('customerId') customerId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createCustomerAddress(customerId, body);
  }

  @Patch('customer-addresses/:addressId')
  updateCustomerAddress(
    @Param('addressId') addressId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateCustomerAddress(addressId, body);
  }

  @Delete('customer-addresses/:addressId')
  deleteCustomerAddress(@Param('addressId') addressId: string) {
    return this.commerceAdminService.deleteCustomerAddress(addressId);
  }

  @Get('customers/:customerId/tags')
  customerTags(
    @Param('customerId') customerId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listCustomerTags(customerId, query);
  }

  @Post('customers/:customerId/tags')
  createCustomerTag(
    @Param('customerId') customerId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createCustomerTag(customerId, body);
  }

  @Delete('customer-tags/:tagId')
  deleteCustomerTag(@Param('tagId') tagId: string) {
    return this.commerceAdminService.deleteCustomerTag(tagId);
  }

  @Get('customers/:customerId/sessions')
  customerSessions(
    @Param('customerId') customerId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listCustomerSessions(customerId, query);
  }

  @Post('customers/:customerId/sessions')
  createCustomerSession(
    @Param('customerId') customerId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createCustomerSession(customerId, body);
  }

  @Patch('customer-sessions/:sessionId/revoke')
  revokeCustomerSession(@Param('sessionId') sessionId: string) {
    return this.commerceAdminService.revokeCustomerSession(sessionId);
  }

  @Get('customers/:customerId/wishlist')
  customerWishlist(
    @Param('customerId') customerId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listCustomerWishlist(customerId, query);
  }

  @Post('customers/:customerId/wishlist')
  createCustomerWishlistItem(
    @Param('customerId') customerId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createCustomerWishlistItem(
      customerId,
      body
    );
  }

  @Delete('customer-wishlist/:wishlistItemId')
  deleteCustomerWishlistItem(@Param('wishlistItemId') wishlistItemId: string) {
    return this.commerceAdminService.deleteCustomerWishlistItem(wishlistItemId);
  }

  @Get('platform/roles')
  roles(@Query() query: RawQuery) {
    return this.commerceAdminService.listRoles(query);
  }

  @Post('platform/roles')
  createRole(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createRole(body);
  }

  @Get('platform/permissions')
  permissions(@Query() query: RawQuery) {
    return this.commerceAdminService.listPermissions(query);
  }

  @Post('platform/permissions')
  createPermission(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createPermission(body);
  }

  @Post('platform/role-permissions')
  assignRolePermission(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.assignRolePermission(body);
  }

  @Delete('platform/role-permissions/:rolePermissionId')
  removeRolePermission(@Param('rolePermissionId') rolePermissionId: string) {
    return this.commerceAdminService.removeRolePermission(rolePermissionId);
  }

  @Get('brands')
  brands(@Query() query: RawQuery) {
    return this.commerceAdminService.listBrands(query);
  }

  @Post('brands')
  createBrand(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createBrand(body);
  }

  @Patch('brands/:brandId')
  updateBrand(
    @Param('brandId') brandId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateBrand(brandId, body);
  }

  @Delete('brands/:brandId')
  deleteBrand(@Param('brandId') brandId: string) {
    return this.commerceAdminService.deleteBrand(brandId);
  }

  @Get('collections')
  collections(@Query() query: RawQuery) {
    return this.commerceAdminService.listCollections(query);
  }

  @Post('collections')
  createCollection(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createCollection(body);
  }

  @Patch('collections/:collectionId')
  updateCollection(
    @Param('collectionId') collectionId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateCollection(collectionId, body);
  }

  @Delete('collections/:collectionId')
  deleteCollection(@Param('collectionId') collectionId: string) {
    return this.commerceAdminService.deleteCollection(collectionId);
  }

  @Post('collections/:collectionId/products')
  addProductToCollection(
    @Param('collectionId') collectionId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.addProductToCollection(collectionId, body);
  }

  @Get('products/:productId/options')
  productOptions(
    @Param('productId') productId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listProductOptions(productId, query);
  }

  @Post('products/:productId/options')
  createProductOption(
    @Param('productId') productId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createProductOption(productId, body);
  }

  @Post('product-options/:optionId/values')
  createProductOptionValue(
    @Param('optionId') optionId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createProductOptionValue(optionId, body);
  }

  @Get('products/:productId/variants')
  productVariants(
    @Param('productId') productId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listProductVariants(productId, query);
  }

  @Post('products/:productId/variants')
  createProductVariant(
    @Param('productId') productId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createProductVariant(productId, body);
  }

  @Patch('products/:productId/variants/:variantId')
  updateProductVariant(
    @Param('productId') productId: string,
    @Param('variantId') variantId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateProductVariant(
      productId,
      variantId,
      body
    );
  }

  @Delete('products/:productId/variants/:variantId')
  deleteProductVariant(
    @Param('productId') productId: string,
    @Param('variantId') variantId: string
  ) {
    return this.commerceAdminService.deleteProductVariant(productId, variantId);
  }

  @Get('products/:productId/images')
  productImages(
    @Param('productId') productId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listProductImages(productId, query);
  }

  @Post('products/:productId/images')
  createProductImage(
    @Param('productId') productId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createProductImage(productId, body);
  }

  @Patch('product-images/:imageId')
  updateProductImage(
    @Param('imageId') imageId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateProductImage(imageId, body);
  }

  @Delete('product-images/:imageId')
  deleteProductImage(@Param('imageId') imageId: string) {
    return this.commerceAdminService.deleteProductImage(imageId);
  }

  @Post('products/:productId/images/upload')
  @UseInterceptors(FileInterceptor('file', { limits: { fileSize: 10 * 1024 * 1024 } }))
  uploadProductImage(
    @Param('productId') productId: string,
    @UploadedFile() file: Express.Multer.File,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.uploadProductImage(productId, file, body);
  }

  @Get('product-images/trash')
  trashedProductImages(@Query() query: RawQuery) {
    return this.commerceAdminService.listTrashedProductImages(query);
  }

  @Delete('product-images/:imageId/purge')
  purgeProductImage(@Param('imageId') imageId: string) {
    return this.commerceAdminService.hardDeleteProductImage(imageId);
  }

  @Get('inventory/locations')
  inventoryLocations(@Query() query: RawQuery) {
    return this.commerceAdminService.listInventoryLocations(query);
  }

  @Post('inventory/locations')
  createInventoryLocation(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createInventoryLocation(body);
  }

  @Post('inventory/items')
  createInventoryItem(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createInventoryItem(body);
  }

  @Get('inventory/items')
  inventoryItems(@Query() query: RawQuery) {
    return this.commerceAdminService.listInventoryItems(query);
  }

  @Patch('inventory/items/:inventoryItemId')
  updateInventoryItem(
    @Param('inventoryItemId') inventoryItemId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateInventoryItem(inventoryItemId, body);
  }

  @Delete('inventory/items/:inventoryItemId')
  deleteInventoryItem(@Param('inventoryItemId') inventoryItemId: string) {
    return this.commerceAdminService.deleteInventoryItem(inventoryItemId);
  }

  @Post('inventory/levels')
  createInventoryLevel(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createInventoryLevel(body);
  }

  @Get('inventory/levels')
  inventoryLevels(@Query() query: RawQuery) {
    return this.commerceAdminService.listInventoryLevels(query);
  }

  @Patch('inventory/levels/:inventoryLevelId')
  updateInventoryLevel(
    @Param('inventoryLevelId') inventoryLevelId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateInventoryLevel(
      inventoryLevelId,
      body
    );
  }

  @Delete('inventory/levels/:inventoryLevelId')
  deleteInventoryLevel(@Param('inventoryLevelId') inventoryLevelId: string) {
    return this.commerceAdminService.deleteInventoryLevel(inventoryLevelId);
  }

  @Post('inventory/levels/:inventoryLevelId/reserve')
  reserveInventoryLevel(
    @Param('inventoryLevelId') inventoryLevelId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.reserveInventoryLevel(
      inventoryLevelId,
      body
    );
  }

  @Post('inventory/levels/:inventoryLevelId/release')
  releaseInventoryLevel(
    @Param('inventoryLevelId') inventoryLevelId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.releaseInventoryLevel(
      inventoryLevelId,
      body
    );
  }

  @Post('inventory/levels/:inventoryLevelId/commit')
  commitInventoryLevel(
    @Param('inventoryLevelId') inventoryLevelId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.commitInventoryLevel(
      inventoryLevelId,
      body
    );
  }

  @Post('inventory/movements')
  createInventoryMovement(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createInventoryMovement(body);
  }

  @Get('inventory/movements')
  inventoryMovements(@Query() query: RawQuery) {
    return this.commerceAdminService.listInventoryMovements(query);
  }

  @Get('master/countries')
  countries(@Query() query: RawQuery) {
    return this.commerceAdminService.listCountries(query);
  }

  @Post('master/countries')
  createCountry(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createCountry(body);
  }

  @Get('master/states')
  states(@Query() query: RawQuery) {
    return this.commerceAdminService.listStates(query);
  }

  @Post('master/states')
  createState(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createState(body);
  }

  @Get('master/cities')
  cities(@Query() query: RawQuery) {
    return this.commerceAdminService.listCities(query);
  }

  @Post('master/cities')
  createCity(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createCity(body);
  }

  @Get('master/currencies')
  currencies(@Query() query: RawQuery) {
    return this.commerceAdminService.listCurrencies(query);
  }

  @Post('master/currencies')
  createCurrency(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createCurrency(body);
  }

  @Get('master/tax-slab-profiles')
  taxSlabProfiles(@Query() query: RawQuery) {
    return this.commerceAdminService.listTaxSlabProfiles(query);
  }

  @Post('master/tax-slab-profiles')
  createTaxSlabProfile(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createTaxSlabProfile(body);
  }

  @Patch('master/tax-slab-profiles/:taxProfileId')
  updateTaxSlabProfile(
    @Param('taxProfileId') taxProfileId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateTaxSlabProfile(taxProfileId, body);
  }

  @Delete('master/tax-slab-profiles/:taxProfileId')
  deleteTaxSlabProfile(@Param('taxProfileId') taxProfileId: string) {
    return this.commerceAdminService.deleteTaxSlabProfile(taxProfileId);
  }

  @Get('store/gst-settings')
  gstSettings() {
    return this.commerceAdminService.getGstSettings();
  }

  @Patch('store/gst-settings')
  updateGstSettings(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.updateGstSettings(body);
  }

  @Get('store/tcs-settings')
  tcsSettings() {
    return this.commerceAdminService.getTcsSettings();
  }

  @Patch('store/tcs-settings')
  updateTcsSettings(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.updateTcsSettings(body);
  }

  @Get('store/tax-report')
  taxReport(@Query() query: RawQuery) {
    return this.commerceAdminService.getTaxReport(query);
  }

  @Get('store/settings')
  storeSettings(@Query() query: RawQuery) {
    return this.commerceAdminService.listStoreSettings(query);
  }

  @Post('store/settings')
  upsertStoreSetting(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.upsertStoreSetting(body);
  }

  @Get('store/domains')
  storeDomains(@Query() query: RawQuery) {
    return this.commerceAdminService.listStoreDomains(query);
  }

  @Post('store/domains')
  createStoreDomain(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createStoreDomain(body);
  }

  @Patch('store/domains/:domainId')
  updateStoreDomain(
    @Param('domainId') domainId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateStoreDomain(domainId, body);
  }

  @Delete('store/domains/:domainId')
  deleteStoreDomain(@Param('domainId') domainId: string) {
    return this.commerceAdminService.deleteStoreDomain(domainId);
  }

  @Get('store/theme-settings')
  themeSettings(@Query() query: RawQuery) {
    return this.commerceAdminService.listThemeSettings(query);
  }

  @Post('store/theme-settings')
  upsertThemeSetting(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.upsertThemeSetting(body);
  }

  @Get('store/pages')
  storePages(@Query() query: RawQuery) {
    return this.commerceAdminService.listStorePages(query);
  }

  @Post('store/pages')
  createStorePage(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createStorePage(body);
  }

  @Patch('store/pages/:pageId')
  updateStorePage(
    @Param('pageId') pageId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateStorePage(pageId, body);
  }

  @Delete('store/pages/:pageId')
  deleteStorePage(@Param('pageId') pageId: string) {
    return this.commerceAdminService.deleteStorePage(pageId);
  }

  @Get('store/navigation-menus')
  navigationMenus(@Query() query: RawQuery) {
    return this.commerceAdminService.listNavigationMenus(query);
  }

  @Post('store/navigation-menus')
  upsertNavigationMenu(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.upsertNavigationMenu(body);
  }

  @Post('store/payment-providers')
  upsertPaymentProvider(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.upsertPaymentProvider(body);
  }

  @Get('store/payment-providers')
  paymentProviders(@Query() query: RawQuery) {
    return this.commerceAdminService.listPaymentProviders(query);
  }

  @Patch('store/payment-providers/:paymentProviderId')
  updatePaymentProvider(
    @Param('paymentProviderId') paymentProviderId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updatePaymentProvider(
      paymentProviderId,
      body
    );
  }

  @Delete('store/payment-providers/:paymentProviderId')
  deletePaymentProvider(@Param('paymentProviderId') paymentProviderId: string) {
    return this.commerceAdminService.deletePaymentProvider(paymentProviderId);
  }

  @Get('store/shipping-zones')
  shippingZones(@Query() query: RawQuery) {
    return this.commerceAdminService.listShippingZones(query);
  }

  @Post('store/shipping-zones')
  createShippingZone(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createShippingZone(body);
  }

  @Patch('store/shipping-zones/:shippingZoneId')
  updateShippingZone(
    @Param('shippingZoneId') shippingZoneId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateShippingZone(shippingZoneId, body);
  }

  @Delete('store/shipping-zones/:shippingZoneId')
  deleteShippingZone(@Param('shippingZoneId') shippingZoneId: string) {
    return this.commerceAdminService.deleteShippingZone(shippingZoneId);
  }

  @Get('store/shipping-rates')
  shippingRates(@Query() query: RawQuery) {
    return this.commerceAdminService.listShippingRates(query);
  }

  @Post('store/shipping-rates')
  createShippingRate(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createShippingRate(body);
  }

  @Patch('store/shipping-rates/:shippingRateId')
  updateShippingRate(
    @Param('shippingRateId') shippingRateId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateShippingRate(shippingRateId, body);
  }

  @Delete('store/shipping-rates/:shippingRateId')
  deleteShippingRate(@Param('shippingRateId') shippingRateId: string) {
    return this.commerceAdminService.deleteShippingRate(shippingRateId);
  }

  @Get('orders/:orderId/payments')
  orderPayments(@Param('orderId') orderId: string, @Query() query: RawQuery) {
    return this.commerceAdminService.listOrderPayments(orderId, query);
  }

  @Get('orders/:orderId/shipments')
  orderShipments(@Param('orderId') orderId: string, @Query() query: RawQuery) {
    return this.commerceAdminService.listOrderShipments(orderId, query);
  }

  @Post('orders/:orderId/shipments')
  createShipment(
    @Param('orderId') orderId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createShipment(orderId, body);
  }

  @Patch('shipments/:shipmentId')
  updateShipment(
    @Param('shipmentId') shipmentId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateShipment(shipmentId, body);
  }

  @Get('orders/:orderId/refunds')
  orderRefunds(@Param('orderId') orderId: string, @Query() query: RawQuery) {
    return this.commerceAdminService.listOrderRefunds(orderId, query);
  }

  @Post('orders/:orderId/refunds')
  createRefund(
    @Param('orderId') orderId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createRefund(orderId, body);
  }

  @Patch('orders/:orderId/payment-status')
  updateOrderPaymentStatus(
    @Param('orderId') orderId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateOrderPaymentStatus(orderId, body);
  }

  @Patch('orders/:orderId/fulfillment-status')
  updateOrderFulfillmentStatus(
    @Param('orderId') orderId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateOrderFulfillmentStatus(
      orderId,
      body
    );
  }

  @Get('marketing/discount-codes')
  discountCodes(@Query() query: RawQuery) {
    return this.commerceAdminService.listDiscountCodes(query);
  }

  // Declared before the /:discountCodeId routes below so a code like "check"
  // itself could never collide with a param route — mirrors the route-order
  // fix already applied to platform/organizations for the same reason.
  @Get('marketing/discount-codes/check')
  checkDiscountCode(@Query('code') code: string) {
    return this.commerceAdminService.checkDiscountCode(code ?? '');
  }

  @Post('marketing/discount-codes')
  createDiscountCode(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createDiscountCode(body);
  }

  @Patch('marketing/discount-codes/:discountCodeId')
  updateDiscountCode(
    @Param('discountCodeId') discountCodeId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updateDiscountCode(discountCodeId, body);
  }

  @Delete('marketing/discount-codes/:discountCodeId')
  deleteDiscountCode(@Param('discountCodeId') discountCodeId: string) {
    return this.commerceAdminService.deleteDiscountCode(discountCodeId);
  }

  @Post('marketing/discount-codes/preview')
  previewDiscountCode(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.previewDiscountCode(body);
  }

  @Get('marketing/promotions')
  promotions(@Query() query: RawQuery) {
    return this.commerceAdminService.listPromotions(query);
  }

  @Post('marketing/promotions')
  createPromotion(@Body() body: Record<string, unknown>) {
    return this.commerceAdminService.createPromotion(body);
  }

  @Patch('marketing/promotions/:promotionId')
  updatePromotion(
    @Param('promotionId') promotionId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updatePromotion(promotionId, body);
  }

  @Delete('marketing/promotions/:promotionId')
  deletePromotion(@Param('promotionId') promotionId: string) {
    return this.commerceAdminService.deletePromotion(promotionId);
  }

  @Post('marketing/promotions/:promotionId/rules')
  createPromotionRule(
    @Param('promotionId') promotionId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.createPromotionRule(promotionId, body);
  }

  @Get('marketing/promotions/:promotionId/rules')
  promotionRules(
    @Param('promotionId') promotionId: string,
    @Query() query: RawQuery
  ) {
    return this.commerceAdminService.listPromotionRules(promotionId, query);
  }

  @Patch('marketing/promotion-rules/:promotionRuleId')
  updatePromotionRule(
    @Param('promotionRuleId') promotionRuleId: string,
    @Body() body: Record<string, unknown>
  ) {
    return this.commerceAdminService.updatePromotionRule(promotionRuleId, body);
  }

  @Delete('marketing/promotion-rules/:promotionRuleId')
  deletePromotionRule(@Param('promotionRuleId') promotionRuleId: string) {
    return this.commerceAdminService.deletePromotionRule(promotionRuleId);
  }

  // Audit reads moved to AuditAdminController (@Controller('admin/audit')).
  // They registered the SAME paths as that controller, so which handler
  // answered depended on module import order — and the copy here supported
  // neither the batch grouping nor the status-range/date/search filters the
  // admin page sends, so a request landing on it silently returned unfiltered
  // rows. AuditQueryService also fails closed on missing store context, which
  // this controller's `?? 'store_demo'` fallback does not.
}

@ApiTags('Storefront')
@Controller('storefront')
export class PublicStorefrontConfigController {
  constructor(private readonly commerceAdminService: CommerceAdminService) {}

  @Get('settings')
  settings() {
    return this.commerceAdminService.listStoreSettings();
  }

  @Get('theme-settings/:themeKey')
  themeSetting(@Param('themeKey') themeKey: string) {
    return this.commerceAdminService.findThemeSetting(themeKey);
  }

  @Get('navigation-menus/:handle')
  navigationMenu(@Param('handle') handle: string) {
    return this.commerceAdminService.findNavigationMenu(handle);
  }

  @Get('pages/:slug')
  page(@Param('slug') slug: string) {
    return this.commerceAdminService.findStorePage(slug);
  }
}
